import React, { useState } from 'react';
import { Globe, Copy, Trash2 } from 'lucide-react';

const HttpHeaderParserTool = () => {
  const [input, setInput] = useState('');
  const [parsed, setParsed] = useState(null);
  const [copied, setCopied] = useState(false);

  const parseHeaders = () => {
    if (!input.trim()) {
      setParsed(null);
      return;
    }

    const lines = input.trim().split('\n');
    const headers = {};
    let requestLine = null;
    let statusLine = null;

    lines.forEach((line, index) => {
      line = line.trim();
      if (!line) return;

      // First line could be request or status line
      if (index === 0) {
        // Request line: GET /path HTTP/1.1
        const requestMatch = line.match(/^(GET|POST|PUT|DELETE|PATCH|HEAD|OPTIONS)\s+(.+)\s+(HTTP\/[\d.]+)$/i);
        if (requestMatch) {
          requestLine = {
            method: requestMatch[1].toUpperCase(),
            path: requestMatch[2],
            version: requestMatch[3]
          };
          return;
        }
        // Status line: HTTP/1.1 200 OK
        const statusMatch = line.match(/^(HTTP\/[\d.]+)\s+(\d{3})\s*(.*)$/i);
        if (statusMatch) {
          statusLine = {
            version: statusMatch[1],
            code: parseInt(statusMatch[2]),
            message: statusMatch[3]
          };
          return;
        }
      }

      // Header line: Key: Value
      const colonIndex = line.indexOf(':');
      if (colonIndex > 0) {
        const key = line.substring(0, colonIndex).trim();
        const value = line.substring(colonIndex + 1).trim();
        headers[key] = value;
      }
    });

    // Analyze headers
    const analysis = analyzeHeaders(headers);

    setParsed({
      requestLine,
      statusLine,
      headers,
      analysis,
      count: Object.keys(headers).length
    });
  };

  const analyzeHeaders = (headers) => {
    const analysis = [];
    const lowerHeaders = {};
    Object.entries(headers).forEach(([k, v]) => {
      lowerHeaders[k.toLowerCase()] = v;
    });

    // Content-Type analysis
    if (lowerHeaders['content-type']) {
      const ct = lowerHeaders['content-type'];
      if (ct.includes('json')) analysis.push({ type: 'info', text: 'JSON content type detected' });
      if (ct.includes('html')) analysis.push({ type: 'info', text: 'HTML content type detected' });
      if (ct.includes('xml')) analysis.push({ type: 'info', text: 'XML content type detected' });
    }

    // Security headers
    if (!lowerHeaders['x-content-type-options']) {
      analysis.push({ type: 'warning', text: 'Missing X-Content-Type-Options header (security)' });
    }
    if (!lowerHeaders['x-frame-options']) {
      analysis.push({ type: 'warning', text: 'Missing X-Frame-Options header (clickjacking protection)' });
    }
    if (lowerHeaders['strict-transport-security']) {
      analysis.push({ type: 'success', text: 'HSTS enabled' });
    }

    // Caching
    if (lowerHeaders['cache-control']) {
      analysis.push({ type: 'info', text: `Caching: ${lowerHeaders['cache-control']}` });
    }

    // Compression
    if (lowerHeaders['content-encoding']) {
      analysis.push({ type: 'success', text: `Compression: ${lowerHeaders['content-encoding']}` });
    }

    // CORS
    if (lowerHeaders['access-control-allow-origin']) {
      const origin = lowerHeaders['access-control-allow-origin'];
      if (origin === '*') {
        analysis.push({ type: 'warning', text: 'CORS allows all origins (*)' });
      } else {
        analysis.push({ type: 'info', text: `CORS origin: ${origin}` });
      }
    }

    return analysis;
  };

  const copyAsJson = () => {
    if (!parsed) return;
    const json = JSON.stringify(parsed.headers, null, 2);
    navigator.clipboard.writeText(json);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const loadSample = (type) => {
    const samples = {
      request: `GET /api/users HTTP/1.1
Host: api.example.com
Accept: application/json
Authorization: Bearer eyJhbGciOiJIUzI1NiIs...
User-Agent: Mozilla/5.0
Accept-Language: en-US,en;q=0.9
Cache-Control: no-cache`,
      response: `HTTP/1.1 200 OK
Content-Type: application/json; charset=utf-8
Content-Length: 1234
Cache-Control: max-age=3600
X-Content-Type-Options: nosniff
X-Frame-Options: DENY
Strict-Transport-Security: max-age=31536000
Access-Control-Allow-Origin: https://example.com
Date: Wed, 15 Mar 2024 12:00:00 GMT`
    };
    setInput(samples[type]);
  };

  const getStatusColor = (code) => {
    if (code >= 200 && code < 300) return 'text-green-400';
    if (code >= 300 && code < 400) return 'text-blue-400';
    if (code >= 400 && code < 500) return 'text-yellow-400';
    if (code >= 500) return 'text-red-400';
    return 'text-gray-400';
  };

  return (
    <div className="space-y-6">
      <div className="flex gap-2 mb-2">
        <button
          onClick={() => loadSample('request')}
          className="text-xs px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded text-gray-300"
        >
          Load Request Sample
        </button>
        <button
          onClick={() => loadSample('response')}
          className="text-xs px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded text-gray-300"
        >
          Load Response Sample
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">HTTP Headers</label>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Paste HTTP request or response headers..."
            className="w-full h-64 bg-gray-800 border border-gray-700 rounded-lg p-4 text-white font-mono text-sm placeholder-gray-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Parsed Headers</label>
          <div className="w-full h-64 bg-gray-800 border border-gray-700 rounded-lg p-4 overflow-auto">
            {parsed ? (
              <div className="space-y-3">
                {parsed.requestLine && (
                  <div className="pb-2 border-b border-gray-700">
                    <span className="text-purple-400 font-bold">{parsed.requestLine.method}</span>
                    <span className="text-white ml-2">{parsed.requestLine.path}</span>
                    <span className="text-gray-500 ml-2">{parsed.requestLine.version}</span>
                  </div>
                )}
                {parsed.statusLine && (
                  <div className="pb-2 border-b border-gray-700">
                    <span className="text-gray-500">{parsed.statusLine.version}</span>
                    <span className={`ml-2 font-bold ${getStatusColor(parsed.statusLine.code)}`}>
                      {parsed.statusLine.code}
                    </span>
                    <span className="text-white ml-2">{parsed.statusLine.message}</span>
                  </div>
                )}
                <div className="space-y-1">
                  {Object.entries(parsed.headers).map(([key, value]) => (
                    <div key={key} className="text-sm">
                      <span className="text-blue-400">{key}:</span>
                      <span className="text-gray-300 ml-2 break-all">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <span className="text-gray-500 text-sm">Parsed headers will appear here...</span>
            )}
          </div>
        </div>
      </div>

      <button
        onClick={parseHeaders}
        className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
      >
        <Globe size={18} />
        Parse Headers
      </button>

      {parsed && parsed.analysis.length > 0 && (
        <div className="p-4 bg-gray-800/50 rounded-lg">
          <h3 className="text-sm font-medium text-gray-300 mb-3">Analysis</h3>
          <div className="space-y-2">
            {parsed.analysis.map((item, i) => (
              <div
                key={i}
                className={`text-sm px-3 py-2 rounded ${
                  item.type === 'success' ? 'bg-green-900/30 text-green-400' :
                  item.type === 'warning' ? 'bg-yellow-900/30 text-yellow-400' :
                  'bg-blue-900/30 text-blue-400'
                }`}
              >
                {item.text}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-3">
        <button
          onClick={copyAsJson}
          disabled={!parsed}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
        >
          <Copy size={16} />
          {copied ? 'Copied!' : 'Copy as JSON'}
        </button>
        <button
          onClick={() => { setInput(''); setParsed(null); }}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
        >
          <Trash2 size={16} />
          Clear
        </button>
      </div>
    </div>
  );
};

export default HttpHeaderParserTool;
